import React, { useEffect, useState } from 'react';
import { fetchStableDBRData, fetchEodSupportData, fetchSectorList } from 'service/service';
import { Box, Stack, Tabs, Tab } from '@mui/material';
import moment from 'moment';
import PageHeader from 'components/Header/Page';
import { useLocation, useNavigate } from 'react-router-dom';
import SectorFilter from 'components/Dropdown/SectorFilter';
import CustomDropdown from 'components/Dropdown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageWrapper from 'components/Common/PageWraper';
import { CustomizedDataGrid } from 'components/customized_data_grid';

const regions = [
    'ALL',
    'Brazil South',
    'Australia East',
    'UK South',
    'South Central US',
    'East US',
    'Germany West Central'
];
const regionOptions = regions.map(location => ({ label: location, value: location }));

const envOptions = [
    { label: 'ALL', value: 'both' },
    { label: 'PROD', value: 'prod' },
    { label: 'PRE-PROD', value: 'preprod' },
    { label: 'QA', value: 'qa' },
    { label: 'Development', value: 'dev' },
    { label: 'Sandbox', value: 'sandbox' }
];

const columnMappingSupport: Record<string, string> = {
    "version": "Version",
    "major_version": "Major Version",
    "spark_version": "Apache Spark Version",
    "release_date": "Release Date",
    "end_of_support_date": "Support End Date"
};

const columnMappingStable: Record<string, string> = {
    "version": "Version",
    "status": "Status",
    "created_at": "Created At",
    "updated_at": "Updated At"
};

const defaultColumnsSupport = ['Version', 'Major Version', 'Apache Spark Version', 'Release Date', 'Support End Date'];
const defaultColumnsStable = ['Version', 'Status', 'Created At', 'Updated At'];

const generateFixedColumns = (mapping: Record<string, string>) => {
    return Object.entries(mapping).map(([key, displayName]) => ({
        key,
        name: displayName,
        sortable: true,
        resizable: true,
        width: 'auto'
    }));
};

const filterTableData = (data: any, mapping: Record<string, string>) => {
    let dataArray: any[] = [];
    if (Array.isArray(data)) {
        dataArray = data;
    } else if (data && typeof data === 'object') {
        dataArray = [data];
    }

    return dataArray.map((row: any) => {
        const filtered: Record<string, any> = {};
        Object.keys(mapping).forEach((key) => {
            if (key.toLowerCase().includes('date') || key.toLowerCase().includes('_at')) {
                filtered[key] = row[key] ? moment(row[key]).format("MM-DD-YYYY") : "NA";
            } else {
                filtered[key] = row[key] !== undefined && row[key] !== null ? row[key] : "NA";
            }
        });
        return filtered;
    });
};

const DBRTabTable = () => {
    const routerLocation = useLocation();
    const navigate = useNavigate();

    const {
        startDate: navStartDate,
        endDate: navEndDate,
        env: navEnv,
        sector: navSector,
        location: navLocation,
        selectedDateInString: navSelectedDate
    } = routerLocation.state || {};

    const [startDate] = useState<string>(navStartDate || moment().subtract(3, 'months').startOf('day').format('YYYY-MM-DD HH:mm'));
    const [endDate] = useState<string>(navEndDate || moment().format('YYYY-MM-DD HH:mm'));
    const [env, setEnv] = useState(navEnv || 'prod');
    const [sectorName, setSectorName] = useState(navSector || 'all');
    const [sector, setSector] = useState(navSector || 'all');
    const [location] = useState<string>(navLocation || 'South Central US');
    const [selectedDateInString] = useState<string>(navSelectedDate || '3M');

    const [tableData, setTableData] = useState<any[]>([]);
    const [columns, setColumns] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
    const [paginationData, setPaginationData] = useState({
        totalElements: 0,
        totalPages: 0
    });
    const [loader, setLoader] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
        setPage(1);
    };

    const handleSectorChange = (value: string) => {
        setSector(value);
        setSectorName(value);
    };

    const [sectorList, setSectorList] = useState<string[]>([]);
    const fetchSectors = async () => {
        const resp = await fetchSectorList({});
        if (resp?.success) {
            setSectorList(resp?.data || []);
        }
    };

    useEffect(() => {
        fetchSectors();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoader(true);
            const mapping = selectedTab === 0 ? columnMappingSupport : columnMappingStable;
            const resp = selectedTab === 0
                ? await fetchEodSupportData({ startDate, endDate, env, sector, location, selectedDateInString, page, limit: pageSize })
                : await fetchStableDBRData({ startDate, endDate, env, sector, location, selectedDateInString, page, limit: pageSize });

            if (resp?.success) {
                let data = resp.data ?? [];
                // Handle cases where data is paginated or wrapped in an object
                if (!Array.isArray(data) && data.docs && Array.isArray(data.docs)) {
                    data = data.docs;
                }

                const filteredData = filterTableData(data, mapping);
                setTableData(filteredData);
                setColumns(generateFixedColumns(mapping));

                let totalElements = 0;
                if (!Array.isArray(resp.data) && resp.data?.totalDocs !== undefined) {
                    totalElements = resp.data.totalDocs;
                } else if (Array.isArray(data)) {
                    totalElements = data.length;
                }

                setPaginationData({
                    totalElements: totalElements,
                    totalPages: Math.ceil(totalElements / pageSize)
                });
            } else {
                setTableData([]);
                setColumns(generateFixedColumns(mapping));
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
            setLoader(false);
        };
        fetchData();
    }, [startDate, endDate, env, sector, location, selectedDateInString, page, pageSize, sortingInfo, selectedTab]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    const handleSorting = (info: any) => {
        setSortingInfo(info);
    };

    const handleExportRequest = async () => {
        const resp = selectedTab === 0
            ? await fetchEodSupportData({ startDate, endDate, env, sector, location, selectedDateInString, page: undefined, limit: undefined, exportType: 'excel' })
            : await fetchStableDBRData({ startDate, endDate, env, sector, location, selectedDateInString, page: undefined, limit: undefined, exportType: 'excel' });

        if (resp?.success && resp?.data) {
            const { buffer, filename, contentType } = resp.data;
            if (buffer && buffer.data) {
                const byteArray = new Uint8Array(buffer.data);
                const blob = new Blob([byteArray], { type: contentType });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        }
    };

    return (
        <PageWrapper>
            <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="space-between" sx={{ mb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', pb: 0 }}>
                    <ArrowBackIcon
                        sx={{
                            color: '#003771',
                            cursor: 'pointer',
                            mr: 2,
                            mb: 1.5 // Align icon with text better when flex-end is used
                        }}
                        onClick={() => navigate(-1)}
                    />
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 'auto',
                            '& .MuiTabs-indicator': {
                                display: 'none',
                            },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1.125rem',
                                color: '#003771 !important',
                                minWidth: 'auto',
                                mr: 5,
                                padding: '10px 0',
                                opacity: 0.5,
                                transition: 'all 0.3s ease',
                                backgroundColor: 'transparent !important',
                                minHeight: 'auto',
                                borderBottom: '9px solid transparent',
                                '&.Mui-selected': {
                                    opacity: 1,
                                    borderBottom: `9px solid #46adfcff`,
                                    backgroundColor: 'transparent !important',
                                },
                                '&:hover': {
                                    opacity: 0.8,
                                    backgroundColor: 'transparent !important',
                                },
                            },
                        }}
                    >
                        <Tab label="Support End Date" />
                        <Tab label="Stable Versions" />
                    </Tabs>
                </Box>
                <Box sx={{ pb: 1 }}>
                    <PageHeader title=" " icon={''} lastSync={null}>
                        <CustomDropdown
                            handleChange={(event) => {
                                const selected = event.target.value;
                                setEnv(selected as string);
                            }}
                            value={env}
                            options={envOptions}
                        />
                        <SectorFilter
                            sectorName={sectorName}
                            setSectorName={setSectorName}
                            sectorOptionsList={sectorList}
                            handleChange={handleSectorChange}
                        />
                    </PageHeader>
                </Box>
            </Stack>
            <Box sx={{ borderBottom: '2px solid #46adfcff', mb: 1, width: '100%', mt: 0 }} />
            <CustomizedDataGrid
                title=""
                tableColumns={columns}
                tableData={tableData}
                tableType={selectedTab === 0 ? "support-end-date" : "stable-versions"}
                defaultColumnNames={selectedTab === 0 ? defaultColumnsSupport : defaultColumnsStable}
                onSorting={handleSorting}
                isSortIcon={true}
                currSorting={sortingInfo}
                page={page}
                pageSize={pageSize}
                paginationData={paginationData}
                listLength={tableData.length}
                handlepageSizeChange={handlePageSizeChange}
                handlePageChange={handlePageChange}
                isDateRangeFilter={false}
                isDisabled={false}
                isFetching={loader}
                showExport={true}
                exportTableData={handleExportRequest}
            />
        </PageWrapper>
    );
};

export default DBRTabTable;
